import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { Box } from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";

import constants from "../../assets/constants";
import * as BotpressAPI from "../../data/api/omniBotpressAPI.js";
import * as BotpressSessionActions from "../../state/redux/botpressSession";
import { deepCopyObject, handleDownloadConversation } from "../../utils/helpers.js";
import AlertDialog from "../dialogs/alertDialog.js";
import BotpressBatchRunDialog from "../dialogs/botpressBatchRunDialog.js";
import BotpressConversationDialog from "../dialogs/botpressConversationDialog.js";
import BotpressInteractiveAssessmentDialog from "../dialogs/botpressInteractiveAssessmentDialog.js";
import TextInputDialog from "../dialogs/textInputDialog.js";
import BotpressConversationsTable from "../ui/botpressConversationsTable.js";
import Widget from "../ui/widget.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressConversations = props => {
  const {
    flaggedChats,
    searchFlaggedChatsRequest,
    flaggedChatPagination,
    flaggedChatPageLoading,
    deleteChatRequest,
    loadConversationRequest,
    setRerunConversationIdRequest,
    analyticsBotODM,
    botpressBotODM,
    testBotODM,
    configurations,
    testConfigurations,
    assessmentPagination,
    searchAssessmentRequest,
    awsGroup,
    userODM,
    conversation,
    conversationFeedback,
    updateChatRequest,
    deleteFeedbackRequest,
    updateFeedbackRequest,
    widgetSize = "large",
    minHeight = "333px",
  } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [rows, setRows] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [downloadConversation, setDownloadConversation] = useState(null);
  const [confirmUnflag, setConfirmUnflag] = useState(null);
  const [showBatchRun, setShowBatchRun] = useState(false);

  const performToggleTracking = theOriginal => {
    //this is null initially then false when set
    if (theOriginal.tracking === true) {
      theOriginal.tracking = false;
    } else {
      theOriginal.tracking = true;
    }
    updateChatRequest({ chatRecords: [theOriginal] });
  };

  const toggleTracking = theOriginal => {
    //unflagging is destructive-ish, so confirm before removing the flag
    if (theOriginal.tracking === true) {
      setConfirmUnflag(theOriginal);
    } else {
      performToggleTracking(theOriginal);
    }
  };

  const onRerunConversation = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      let conversationNumber = conversationRow.id;
      setRerunConversationIdRequest({
        rerunConversationId: conversationId,
        rerunConversationNumber: conversationNumber.toString(),
        rerunFeedbackNumber: null,
        // These conversations live in the PROD (botpressBotODM) bot.
        rerunIsTestBot: false,
      });

      if (awsGroup === constants.userGroups.Administrator) {
        props.history.push(constants.appPaths.appPathAdminBotpressTestHarness);
      } else if (awsGroup === constants.userGroups.Provider) {
        props.history.push(constants.appPaths.appPathProviderBotpressTestHarness);
      } else if (awsGroup === constants.userGroups.Partner) {
        props.history.push(constants.appPaths.appPathPartnerBotpressTestHarness);
      }

      //switcdh
    } catch (error) {
      console.error("Error getting table rows", error);
    }
  };

  useEffect(() => {
    if (flaggedChats != null) {
      setRows(flaggedChats);
    } else {
      setRows([]);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flaggedChats]);

  useEffect(() => {
    if (selectedEntry != null) {
      loadConversationRequest(selectedEntry.conversationId);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntry]);

  const onRequestNewPage = async pagination => {
    try {
      if (flaggedChatPageLoading) {
        return;
      }
      //add dates right here?
      searchFlaggedChatsRequest(pagination);
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  const onHandleDeveloperLogs = conversationRow => {
    try {
      if (conversationRow == null) {
        return;
      }

      let conversationId = conversationRow.conversationId;
      const theURL = `https://app.botpress.cloud/workspaces/${botpressBotODM.omniData.workspaceId}/bots/${botpressBotODM.omniData.botId}/conversations/${conversationId}/logs`;
      window.open(theURL, "_blank");

      //got to page.
    } catch (error) {
      console.error("Error getting table rows", error);
      setRows([]);
    }
  };

  //#region analysis conversation

  const [assessConversationEntry, setAssessConversationEntry] = useState(null);
  const [singleConversation, setSingleConversation] = useState(null);

  useEffect(() => {
    if (assessConversationEntry != null) {
      try {
        const credentialedPayload = {
          botODM: botpressBotODM,
          conversationId: assessConversationEntry.conversationId,
        };
        BotpressAPI.loadConversation(credentialedPayload).then(returnedConversation => {
          setSingleConversation(returnedConversation);
        });
      } catch (error) {
        console.error("Error loading conversation for assessment", error);
      }
    } else {
      setSingleConversation(null);
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessConversationEntry]);
  const onRefreshTable = () => {
    let newPagination = deepCopyObject(flaggedChatPagination);
    newPagination["invalidateCache"] = true;
    onRequestNewPage(newPagination);
  };

  // After a batch run creates assessments on the TEST bot, refresh the
  // Assessments widget so the new flagged-rerun assessments appear.
  const onFlaggedRunAssessmentsCreated = () => {
    let newPagination = deepCopyObject(assessmentPagination);
    newPagination["botODM"] = testBotODM;
    newPagination["invalidateCache"] = true;
    searchAssessmentRequest(newPagination);
  };
  //#endregion

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={"Flagged Conversations"}
        text={`${flaggedChatPagination.totalItems} Instances Total`}
      />

      <WidgetSurveyContent>
        <BotpressConversationsTable
          data={rows}
          isLoading={flaggedChatPageLoading}
          onEditEntry={setSelectedEntry}
          onDownloadEntry={setDownloadConversation}
          onRequestNewPage={onRequestNewPage}
          totalItemCount={flaggedChatPagination.totalItems}
          onAssessEntry={analyticsBotODM ? setAssessConversationEntry : null}
          onHandleDeveloperLogs={onHandleDeveloperLogs}
          onRerunConversation={onRerunConversation}
          onRunAllFlagged={() => setShowBatchRun(true)}
          toggleTracking={toggleTracking}
          onRefreshTable={onRefreshTable}
          refreshTooltipTitle="Refresh flagged conversations"
        />
      </WidgetSurveyContent>

      {/* Skeleton overlay while a page loads. The table stays mounted underneath
          (it self-fetches on mount/pagination) so we cover it rather than unmount
          it — unmounting would re-trigger the fetch in a loop. */}
      {flaggedChatPageLoading && (
        <Box sx={{ position: "absolute", top: "56px", left: 0, right: 0, bottom: 0, zIndex: 2 }}>
          <WidgetSkeleton />
        </Box>
      )}

      {selectedEntry != null && (
        <BotpressConversationDialog
          flaggedContext={true}
          conversationId={selectedEntry.conversationId}
          conversationNumber={selectedEntry.id}
          conversation={conversation}
          botODM={botpressBotODM}
          dialogTitle={`Conversation #${selectedEntry.id}`}
          conversationFeedback={conversationFeedback}
          deleteFeedbackRequest={deleteFeedbackRequest}
          updateFeedbackRequest={updateFeedbackRequest}
          userODM={userODM}
          open={selectedEntry != null}
          defaultValue={selectedEntry}
          onAccept={() => {
            loadConversationRequest(null);
            setSelectedEntry(null);
          }}
          onClose={() => {
            loadConversationRequest(null);
            setSelectedEntry(null);
          }}
          onDelete={() => {
            deleteChatRequest({ conversationId: selectedEntry.id });
            loadConversationRequest(null);
            setSelectedEntry(null);
            enqueueSnackbar("Conversation Deleted Successfully", {
              variant: "success",
            });
          }}
        />
      )}
      {singleConversation != null && (
        <BotpressInteractiveAssessmentDialog
          userODM={userODM}
          analyticsBotODM={analyticsBotODM}
          open={singleConversation != null}
          singleConversation={singleConversation}
          onAccept={() => {
            setAssessConversationEntry(null);
          }}
          onClose={() => {
            setAssessConversationEntry(null);
          }}
        />
      )}
      {downloadConversation != null && (
        //todo replace this with a new dialog that supports a download format property
        <TextInputDialog
          inputLabel={`File Name`}
          open={downloadConversation}
          dialogTitle={"Download Conversation"}
          dialogMessage={`Please enter a file name for the conversation download`}
          defaultValue={`Conversation_${format(new Date(), "yyMMdd_hhmm")}`}
          inputPlaceholder={"File Name Here"}
          onAccept={theValue => {
            try {
              BotpressAPI.loadConversation({
                botODM: botpressBotODM,
                conversationId: downloadConversation.conversationId,
              }).then(returnedMessages => {
                handleDownloadConversation(returnedMessages.reverse(), theValue.split(".")[0]);
              });

              setDownloadConversation(null);
            } catch (error) {
              console.log(error);
            }
            setDownloadConversation(null);
          }}
          onClose={() => {
            setDownloadConversation(null);
          }}
        />
      )}
      {showBatchRun && (
        <BotpressBatchRunDialog
          open={showBatchRun}
          botODM={testBotODM}
          sourceBotODM={botpressBotODM}
          userODM={userODM}
          configurations={configurations}
          testConfigurations={testConfigurations}
          onAssessmentsCreated={onFlaggedRunAssessmentsCreated}
          onClose={() => setShowBatchRun(false)}
        />
      )}
      {confirmUnflag != null && (
        <AlertDialog
          open={confirmUnflag != null}
          dialogTitle={"Confirm Unflag"}
          dialogMessage={"Are you sure you want to unflag this conversation?"}
          onAccept={() => {
            performToggleTracking(confirmUnflag);
            setConfirmUnflag(null);
          }}
          onClose={() => {
            setConfirmUnflag(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  flaggedChats: state.botpressSession.flaggedChats,
  flaggedChatsStartDate: state.botpressSession.flaggedChatsStartDate,
  flaggedChatsEndDate: state.botpressSession.flaggedChatsEndDate,
  flaggedChatPagination: state.botpressSession.flaggedChatPagination,
  flaggedChatPageLoading: state.botpressSession.flaggedChatPageLoading,

  conversation: state.botpressSession.conversation,
  conversationFeedback: state.botpressSession.conversationFeedback,

  awsGroup: state.userSession.awsGroup,

  userODM: state.userSession.userODM,
  botpressBotODM: state.botpressSession.botpressBotODM,
  testBotODM: state.botpressSession.testBotODM,
  analyticsBotODM: state.botpressSession.analyticsBotODM,
  configurations: state.botpressSession.configurations,
  testConfigurations: state.botpressSession.testConfigurations,
  assessmentPagination: state.botpressSession.assessmentPagination,
});

const mapDispatchToProps = {
  searchFlaggedChatsRequest: BotpressSessionActions.searchFlaggedChatsRequest,
  deleteChatRequest: BotpressSessionActions.deleteChatRequest,
  loadConversationRequest: BotpressSessionActions.loadConversationRequest,
  deleteFeedbackRequest: BotpressSessionActions.deleteFeedbackRequest,
  updateFeedbackRequest: BotpressSessionActions.updateFeedbackRequest,
  setRerunConversationIdRequest: BotpressSessionActions.setRerunConversationIdRequest,
  updateChatRequest: BotpressSessionActions.updateChatRequest,
  searchAssessmentRequest: BotpressSessionActions.searchAssessmentRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BotpressConversations));
